/**
 * Amazon Ads SD fetchMetrics orchestration (Story 19.15, Task 7)
 *
 * The adapter's `fetchMetrics` dispatch routes SD-only requests to
 * `runSdFetchMetrics`. The product-specific bits live here; the heavy
 * pipeline (submit / poll / download / parse) is in shared/report-orchestration.
 *
 * Per Story 19.15 AC #5 / #8 / #9 / #10:
 *   - 30 priority SD metrics across sdCampaigns / sdAdvertisedProduct /
 *     sdPurchasedProduct
 *   - 90-day max date range per chunk (3× SP)
 *   - 3h reporting delay → `dataFreshnessLagHours: 3`
 *   - 72h cache freshness window
 *   - 9-row in-flight safeguard via shared pipeline
 *
 * SD-specific caveats:
 *   - Round 2 Pre-mortem E — `viewabilityRate` denominator is
 *     `viewableImpressions / measurableImpressions` (NOT `/impressions`).
 *     Adapter does NOT derive — emit Amazon's value directly.
 *   - Click + view splits across nearly every conversion metric (purchases /
 *     sales / addToCart / branded searches). Round 3 Vuln 4 — typed column-map
 *     keys per ad-product prevent silent conflation.
 */

import {
  adapterErr,
  adapterOk,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type OAuthCredentials,
} from "../adapter";
import {
  buildAmazonAdsCacheKey,
  buildAmazonAdsReportRequest,
  computeAmazonAdsCacheTtl,
  computeRequestHash,
  detectColumnDrift,
  normalizeRequestForHash,
  validateCachePayload,
  type SpReportRecord,
} from "./reports";
import {
  buildSdColumnList,
  pickSdReportType,
  type SdReportTypeId,
} from "./sd-column-map";
import {
  runAmazonAdsReportPipeline,
  type AmazonAdsKvApi,
  type AmazonAdsReportJobsApi,
} from "./shared/report-orchestration";
import {
  chunkAndAggregate,
  type ChunkResult,
} from "../async-chunking";
import {
  AMAZON_ADS_REGIONAL_HOSTS,
  type AmazonAdsRegion,
} from "./oauth";

/** Story 19.15 AC #3 — SD supports up to 90 days per report. */
export const AMAZON_ADS_SD_MAX_DAYS_PER_REPORT = 90;

/** Story 19.15 AC #8 — SD has a 3h reporting delay. */
export const AMAZON_ADS_SD_DATA_FRESHNESS_LAG_HOURS = 3;

/** Story 19.15 AC #2 — wall-clock budget. */
export const AMAZON_ADS_SD_REPORT_TIMEOUT_MS = 30_000;

export interface RunSdFetchMetricsInput {
  readonly ctx: AdapterContext;
  readonly credentials: OAuthCredentials;
  readonly dateRange: { start: Date; end: Date };
  readonly integration: {
    readonly accountId: string;
    readonly region: AmazonAdsRegion;
  };
  readonly lwaClientId: string;
  readonly metrics?: ReadonlyArray<string>;
  readonly dimensions?: ReadonlyArray<string>;
  readonly jobs: AmazonAdsReportJobsApi;
  readonly kv: AmazonAdsKvApi;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly correlationId?: string;
  readonly newJobId?: () => string;
}

const DEFAULT_SD_METRIC_KEYS = [
  "impressions",
  "clicks",
  "cost",
  "viewabilityRate",
  "purchases",
  "purchasesClicks",
  "sales",
  "salesClicks",
  "unitsSold",
  "addToCart",
  "viewClickThroughRate",
] as const;

export async function runSdFetchMetrics(
  input: RunSdFetchMetricsInput
): Promise<AdapterResult<AdapterMetrics>> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const now = input.now ?? Date.now;
  const sleep =
    input.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const correlationId = input.correlationId ?? crypto.randomUUID();
  const newJobId = input.newJobId ?? (() => crypto.randomUUID());

  const validation = validateRequest(input);
  if (!validation.ok) return adapterErr(validation.error);

  if (!AMAZON_ADS_REGIONAL_HOSTS[input.integration.region]) {
    return adapterErr({
      code: "invalid_response",
      message: `Unknown Amazon Ads region: ${input.integration.region}`,
      retryable: false,
    });
  }

  const dimensions = input.dimensions ?? [];
  const reportType = pickSdReportType(dimensions);
  if (!reportType.ok) {
    return adapterErr({
      code:
        reportType.error === "ad_product_dimension_mismatch"
          ? "invalid_request"
          : "invalid_response",
      message: reportType.message,
      retryable: false,
    });
  }

  const totalDays = daysInclusive(input.dateRange.start, input.dateRange.end);
  const allRecords: SpReportRecord[] = [];
  const allWarnings: string[] = [];

  if (totalDays > AMAZON_ADS_SD_MAX_DAYS_PER_REPORT) {
    const chunkResult = await chunkAndAggregate<SpReportRecord>(
      { startDate: input.dateRange.start, endDate: input.dateRange.end },
      {
        maxDaysPerChunk: AMAZON_ADS_SD_MAX_DAYS_PER_REPORT,
        timeBudgetMs: AMAZON_ADS_SD_REPORT_TIMEOUT_MS,
      },
      (chunk) =>
        runOneSdChunk({
          input,
          fetchImpl,
          now,
          sleep,
          correlationId,
          newJobId,
          reportTypeId: reportType.reportTypeId,
          chunk,
        }),
      now
    );

    if (!chunkResult.success) {
      return adapterErr({
        code: (chunkResult.errorCode as never) ?? "invalid_response",
        message: chunkResult.errorMessage,
        retryable: false,
      });
    }
    if (chunkResult.partial) {
      allWarnings.push(
        `report_chunks_pending: ${chunkResult.chunksCompleted}/${chunkResult.chunksTotal} chunks completed`
      );
    }
    for (const r of chunkResult.records) allRecords.push(r);
    for (const w of chunkResult.warnings) allWarnings.push(w);
  } else {
    const r = await runOneSdChunk({
      input,
      fetchImpl,
      now,
      sleep,
      correlationId,
      newJobId,
      reportTypeId: reportType.reportTypeId,
      chunk: input.dateRange,
    });
    if (r.kind === "error" && r.fatal) {
      return adapterErr({
        code: (r.code as never) ?? "invalid_response",
        message: r.message,
        retryable: false,
      });
    }
    if (r.kind === "ok") {
      for (const rec of r.records) allRecords.push(rec);
      if (r.warnings) for (const w of r.warnings) allWarnings.push(w);
    } else {
      allWarnings.push(`single-chunk non-fatal: ${r.message}`);
    }
  }

  // SD-specific: do NOT compute viewabilityRate (Amazon emits it directly per
  // Round 2 Pre-mortem E — denominator is measurableImpressions, NOT impressions).
  // We do NOT compute clickThroughRate either because SD's relevant rate is
  // viewClickThroughRate (Amazon emits it). Records pass through unchanged
  // except for Round 3 Vuln 3 numeric guards (applied at parse time in the
  // shared NDJSON parser).
  const finalRecords = allRecords;

  return adapterOk({
    platform: "amazon-ads",
    accountId: input.integration.accountId,
    dateRange: input.dateRange,
    records: finalRecords,
    fetchedAt: new Date(now()),
    recordCount: finalRecords.length,
    warnings: allWarnings.map((reason) => ({
      reportType: reportType.reportTypeId,
      reason,
    })),
    metadata: {
      dataFreshnessLagHours: AMAZON_ADS_SD_DATA_FRESHNESS_LAG_HOURS,
      correlationId,
      adProduct: "SPONSORED_DISPLAY",
    },
  });
}

interface RunOneSdChunkArgs {
  readonly input: RunSdFetchMetricsInput;
  readonly fetchImpl: typeof fetch;
  readonly now: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  readonly correlationId: string;
  readonly newJobId: () => string;
  readonly reportTypeId: SdReportTypeId;
  readonly chunk:
    | { start: Date; end: Date }
    | { startDate: Date; endDate: Date };
}

async function runOneSdChunk(
  args: RunOneSdChunkArgs
): Promise<ChunkResult<SpReportRecord>> {
  const start = "start" in args.chunk ? args.chunk.start : args.chunk.startDate;
  const end = "end" in args.chunk ? args.chunk.end : args.chunk.endDate;
  const metrics = args.input.metrics ?? DEFAULT_SD_METRIC_KEYS;
  const dimensions = args.input.dimensions ?? [];

  const built = buildAmazonAdsReportRequest({
    adProduct: "SPONSORED_DISPLAY",
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
  });
  const normalized = normalizeRequestForHash(built.body);
  const requestHash = await computeRequestHash(normalized);

  const namedBuilt = buildAmazonAdsReportRequest({
    adProduct: "SPONSORED_DISPLAY",
    reportTypeId: args.reportTypeId,
    startDate: start,
    endDate: end,
    metrics,
    dimensions,
    requestHashShort: requestHash.slice(0, 8),
  });

  const cacheKey = buildAmazonAdsCacheKey({
    adProduct: "SPONSORED_DISPLAY",
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    requestHash,
  });

  try {
    const cached = await args.input.kv.get(cacheKey);
    if (cached) {
      const parsed = safeJson(cached);
      if (parsed && Array.isArray(parsed.records)) {
        return {
          kind: "ok",
          records: parsed.records as SpReportRecord[],
          warnings: ["cache_hit", ...built.warnings],
        };
      }
    }
  } catch (err) {
    logEvent("amazon_ads_cache_read_failed", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_DISPLAY",
      message: err instanceof Error ? err.message : String(err),
    });
  }

  const result = await runAmazonAdsReportPipeline<SpReportRecord>({
    accessToken: args.input.credentials.accessToken,
    clientId: args.input.lwaClientId,
    profileId: args.input.integration.accountId,
    region: args.input.integration.region,
    tenantId: args.input.ctx.tenantId,
    integrationId: args.input.ctx.integrationId,
    body: namedBuilt.body,
    requestHash,
    correlationId: args.correlationId,
    adProduct: "SPONSORED_DISPLAY",
    reportTypeId: args.reportTypeId,
    jobs: args.input.jobs,
    parseRecord: (r) => r,
    fetchImpl: args.fetchImpl,
    now: args.now,
    sleep: args.sleep,
    newJobId: args.newJobId,
  });

  if (result.kind === "error") {
    return {
      kind: "error",
      message: result.message,
      code: result.code,
      fatal: !result.retryable,
    };
  }
  if (result.kind === "pending") {
    return {
      kind: "error",
      message: result.message,
      fatal: false,
    };
  }

  const records = result.records;
  const warnings: string[] = [...built.warnings, ...result.warnings];

  const requestedColumns = buildSdColumnList(
    args.reportTypeId,
    metrics,
    dimensions
  ).columns;
  const drift = detectColumnDrift(requestedColumns, records);
  if (drift.missingColumns.length > 0) {
    logEvent("amazon_ads_column_drift_detected", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_DISPLAY",
      reportId: result.reportId,
      missingColumns: drift.missingColumns,
    });
    for (const w of drift.warnings) warnings.push(w);
  }

  const cacheValidation = validateCachePayload(records);
  if (cacheValidation.valid) {
    const ttlSec = computeAmazonAdsCacheTtl(
      { startDate: start, endDate: end },
      "SPONSORED_DISPLAY",
      new Date(args.now())
    );
    try {
      await args.input.kv.put(cacheKey, JSON.stringify({ records }), ttlSec);
    } catch (err) {
      logEvent("amazon_ads_cache_write_failed", {
        correlationId: args.correlationId,
        adProduct: "SPONSORED_DISPLAY",
        reportId: result.reportId,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  } else {
    logEvent("amazon_ads_cache_validation_failed", {
      correlationId: args.correlationId,
      adProduct: "SPONSORED_DISPLAY",
      reportId: result.reportId,
      reason: cacheValidation.reason,
    });
  }

  return { kind: "ok", records, warnings };
}

function validateRequest(
  input: RunSdFetchMetricsInput
):
  | { ok: true }
  | {
      ok: false;
      error: { code: "invalid_response"; message: string; retryable: false };
    } {
  if (
    !(input.dateRange.start instanceof Date) ||
    !(input.dateRange.end instanceof Date) ||
    Number.isNaN(input.dateRange.start.getTime()) ||
    Number.isNaN(input.dateRange.end.getTime())
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SD fetchMetrics: dateRange contains invalid Date",
        retryable: false,
      },
    };
  }
  if (input.dateRange.start.getTime() > input.dateRange.end.getTime()) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SD fetchMetrics: dateRange.start > dateRange.end",
        retryable: false,
      },
    };
  }
  if (
    !input.integration.accountId ||
    typeof input.integration.accountId !== "string"
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SD fetchMetrics: integration.accountId (profile ID) is required",
        retryable: false,
      },
    };
  }
  if (!input.lwaClientId) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SD fetchMetrics: AMAZON_ADS_CLIENT_ID env binding missing",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.tenantId !== "string" ||
    input.ctx.tenantId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message:
          "Amazon Ads SD fetchMetrics: ctx.tenantId is required (cross-tenant cache isolation)",
        retryable: false,
      },
    };
  }
  if (
    typeof input.ctx.integrationId !== "string" ||
    input.ctx.integrationId.length === 0
  ) {
    return {
      ok: false,
      error: {
        code: "invalid_response",
        message: "Amazon Ads SD fetchMetrics: ctx.integrationId is required",
        retryable: false,
      },
    };
  }
  return { ok: true };
}

function daysInclusive(start: Date, end: Date): number {
  const startMs = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endMs = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000)) + 1;
}

function safeJson(s: string): { records?: unknown } | null {
  try {
    return JSON.parse(s) as { records?: unknown };
  } catch {
    return null;
  }
}

function logEvent(event: string, details: Record<string, unknown>): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}
